import { ReactNode } from "react";
import { Link } from "react-router-dom";

import { PageHeader } from "@/components/ui/page-header";

const LAST_UPDATED = "27 de abril de 2026";
const COMPANY_NAME = "Blackwise Tecnologia Ltda.";
const COMPANY_CNPJ = "58.153.615/0001-38";
const COMPANY_EMAIL = "cloud@blackwise.tech";

/**
 * Terms of Service mirrored from hotsite (/termos) for the
 * authenticated app.
 */
export function Terms() {
  return (
    <section className="space-y-7 pb-10">
      <PageHeader
        eyebrow="Jurídico"
        title="Termos de Uso"
        lede={`Última atualização: ${LAST_UPDATED}`}
      />

      <article className="space-y-7 text-zinc-700 leading-relaxed">
        <p>
          Estes Termos regem a contratação e utilização da plataforma
          CliniSharp, oferecida em modalidade SaaS por{" "}
          <strong>{COMPANY_NAME}</strong> (CNPJ {COMPANY_CNPJ}). Ao usar a
          Plataforma, você declara ter lido e aceito integralmente este
          documento.
        </p>

        <Section title="1. Definições">
          <ul className="list-disc pl-5 space-y-1">
            <li>
              <strong>CliniSharp / Plataforma</strong>: software, app web e
              mobile, agentes de IA e serviços relacionados.
            </li>
            <li>
              <strong>Cliente / Contratante</strong>: pessoa jurídica ou
              autônoma que contrata um plano.
            </li>
            <li>
              <strong>Usuário</strong>: pessoa física que utiliza a Plataforma
              em nome do Contratante.
            </li>
            <li>
              <strong>Paciente</strong>: indivíduo cadastrado pelo
              Contratante.
            </li>
            <li>
              <strong>Plano</strong>: conjunto de funcionalidades, limites e
              preço contratados.
            </li>
          </ul>
        </Section>

        <Section title="2. Objeto">
          <p>
            Licença não exclusiva, intransferível e revogável de uso da
            Plataforma, conforme o Plano contratado, exclusivamente para
            gestão da clínica.
          </p>
        </Section>

        <Section title="3. Cadastro e segurança da conta">
          <ol className="list-decimal pl-5 space-y-2">
            <li>O Contratante é responsável pela veracidade dos dados.</li>
            <li>Credenciais são pessoais e intransferíveis.</li>
            <li>
              Em caso de uso não autorizado, notificar imediatamente{" "}
              <a
                className="text-teal-600 underline underline-offset-2"
                href={`mailto:${COMPANY_EMAIL}`}
              >
                {COMPANY_EMAIL}
              </a>
              .
            </li>
          </ol>
        </Section>

        <Section title="4. Planos, preços e pagamento">
          <ol className="list-decimal pl-5 space-y-2">
            <li>Planos vigentes disponíveis em /precos.</li>
            <li>Cobrança recorrente, mensal ou anual.</li>
            <li>
              Pagamento por cartão, PIX ou boleto. Tributos serão acrescidos
              quando aplicáveis.
            </li>
            <li>
              Inadimplência superior a 10 dias pode levar à suspensão; acima
              de 30 dias, ao cancelamento e exclusão de dados.
            </li>
          </ol>
        </Section>

        <Section title="5. Período de teste (trial)">
          <p>
            Quando oferecido, o trial gratuito permite avaliar a Plataforma
            com funcionalidades limitadas; ao final, é necessário contratar
            um Plano pago.
          </p>
        </Section>

        <Section title="6. Cancelamento e reembolso">
          <ol className="list-decimal pl-5 space-y-2">
            <li>O Contratante pode cancelar a qualquer momento.</li>
            <li>
              Mensal: cancelamento efetivado ao final do ciclo já pago, sem
              cobrança adicional.
            </li>
            <li>
              Anual à vista: reembolso proporcional ao saldo não utilizado,
              descontados eventuais descontos concedidos.
            </li>
            <li>
              Direito de arrependimento (CDC art. 49): 7 dias corridos, com
              reembolso integral.
            </li>
            <li>
              Após o cancelamento: 30 dias para exportar os dados antes da
              exclusão definitiva.
            </li>
          </ol>
        </Section>

        <Section title="7. Obrigações do Contratante">
          <ul className="list-disc pl-5 space-y-2">
            <li>Cumprir a legislação aplicável (LGPD, ética profissional).</li>
            <li>Obter consentimento dos pacientes quando exigido por lei.</li>
            <li>Não utilizar para fins ilícitos, fraude ou spam.</li>
            <li>Manter backups quando julgar necessário.</li>
          </ul>
        </Section>

        <Section title="8. Propriedade intelectual">
          <ol className="list-decimal pl-5 space-y-2">
            <li>
              Software, código, design, marca e prompts são de propriedade
              exclusiva de {COMPANY_NAME}.
            </li>
            <li>Vedada reprodução, modificação ou engenharia reversa.</li>
            <li>
              Dados inseridos pelo Contratante permanecem de sua propriedade.
              A CliniSharp atua como operadora; ver{" "}
              <Link
                to="/legal/privacidade"
                className="text-teal-600 underline underline-offset-2"
              >
                Política de Privacidade
              </Link>
              .
            </li>
          </ol>
        </Section>

        <Section title="9. Disponibilidade e suporte">
          <p>
            SLA-alvo de 99,5% de uptime mensal, excluídas manutenções
            programadas. Suporte em horário comercial (seg-sex, 9h-18h).
          </p>
        </Section>

        <Section title="10. Limitação de responsabilidade">
          <ol className="list-decimal pl-5 space-y-2">
            <li>
              Sem responsabilidade por danos indiretos, salvo dolo ou culpa
              grave.
            </li>
            <li>
              Limite total de responsabilidade: valor pago nos 12 meses
              anteriores ao evento.
            </li>
            <li>
              Funcionalidades de IA são auxiliares; não substituem a
              avaliação clínica humana.
            </li>
          </ol>
        </Section>

        <Section title="11. Confidencialidade">
          <p>
            As partes mantêm sigilo sobre informações confidenciais por 5
            anos após o término do contrato.
          </p>
        </Section>

        <Section title="12. Vigência e modificações">
          <ol className="list-decimal pl-5 space-y-2">
            <li>Prazo indeterminado, enquanto houver assinatura ativa.</li>
            <li>
              Alterações comunicadas com no mínimo 15 dias de antecedência.
            </li>
            <li>
              Em caso de discordância, é possível cancelar sem ônus,
              respeitados os pagamentos vincendos.
            </li>
          </ol>
        </Section>

        <Section title="13. Foro e legislação">
          <p>
            Legislação brasileira. Foro da Comarca de{" "}
            <strong>Bauru &mdash; SP</strong>, com renúncia a qualquer outro.
          </p>
        </Section>

        <Section title="14. Contato">
          <p>
            <a
              className="text-teal-600 font-medium underline underline-offset-2"
              href={`mailto:${COMPANY_EMAIL}`}
            >
              {COMPANY_EMAIL}
            </a>
          </p>
        </Section>
      </article>
    </section>
  );
}

type SectionProps = { title: string; children: ReactNode };

function Section({ title, children }: SectionProps) {
  return (
    <section className="space-y-3">
      <h2 className="text-lg font-semibold text-zinc-800">
        {title}
      </h2>
      <div>{children}</div>
    </section>
  );
}
